import React, { useState } from 'react'
import { ContentWrapper, SmallValue, Value, Item, ItemActions } from '../styles'
import { parseISO } from 'date-fns'
import { safeFormat } from '@/v2/utils/convert'
import { useTheme } from '@mui/material/styles'
import MoreIcon from '@mui/icons-material/MoreVert'
import Typography from '@/v2/components/common/Typography'
import IconMenu from '@/v2/components/common/IconMenu'
import useModals from '@/v2/hooks/useModals'
import { useRemoveInsurance } from '@/v2/hooks/insurance/useInsurances'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import Button from '@/v2/components/common/Button'
import Dialog from '@mui/material/Dialog'
import { Grid } from '@mui/material'

const InsuranceInfo = ({ insurance, patient, onEligibilityCheckClick }) => {
  const theme = useTheme()
  const { createdAt, payer } = insurance
  const { goTo } = useModals()
  const { onOpenSnackbar } = useSnackbarContext()
  const { removeInsurance } = useRemoveInsurance(patient.id, insurance.id)
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)

  const handleConfirmDelete = async () => {
    const { error } = await removeInsurance()
    if (!error) {
      onOpenSnackbar('Insurance removed', 'success')
    }
    setOpenDeleteConfirmation(false)
  }

  const options = [
    {
      label: 'View',
      onClick: () =>
        goTo(`/v2/patient/${patient.id}/insurance/view`, {
          modalType: 'view',
          insurance,
          patient,
        }),
    },
    {
      label: 'Remove',
      onClick: () => setOpenDeleteConfirmation(true),
    },
  ]

  const handleEligibilityCheck = () => {
    onEligibilityCheckClick()
    goTo(`/v2/patient/${patient.id}/eligibility/check`, {
      modalType: 'create',
      patient,
      insurance: { ...payer },
    })
  }

  return (
    <Item theme={theme.palette}>
      <ContentWrapper>
        <SmallValue>
          <Typography fontSize="14px" sx={{ mb: '2px' }}>
            Created
          </Typography>
          <Typography bold fontSize="14px" sx={{ mb: '2px' }}>
            {safeFormat(parseISO(createdAt), 'MMMM dd, yyyy HH:mm')}
          </Typography>
        </SmallValue>

        <Value>
          <Typography fontSize="14px" sx={{ mb: '2px' }}>
            Insurance
          </Typography>
          <Typography bold fontSize="14px" sx={{ mb: '2px' }}>
            {payer.name}
          </Typography>
        </Value>

        <Value>
          <Typography fontSize="14px" sx={{ mb: '2px' }}>
            ID
          </Typography>
          <Typography bold fontSize="14px" sx={{ mb: '2px' }}>
            {payer.code}
          </Typography>
        </Value>
      </ContentWrapper>

      <ItemActions>
        <Button
          className="patient-messages-button"
          variant="outlined"
          onClick={handleEligibilityCheck}
          size="small"
        >
          Eligibility Check
        </Button>

        <IconMenu
          options={options}
          icon={<MoreIcon fontSize="small" />}
          aria-label="More"
          title="More"
        />
      </ItemActions>

      <Dialog
        open={openDeleteConfirmation}
        onClose={() => setOpenDeleteConfirmation(false)}
      >
        <div style={{ padding: '38px 30px 23px 30px', textAlign: 'center' }}>
          <Typography variant="h3" sx={{ marginBottom: '24px' }}>
            Are you sure you want to remove this insurance?
          </Typography>
          <Typography
            variant="body2"
            sx={{ marginBottom: '24px', color: 'text.secondary' }}
          >
            This action cannot be undone. The insurance information for{' '}
            {payer.name} will be permanently removed.
          </Typography>
          <Grid
            display="flex"
            width={'100%'}
            justifyContent={'flex-end'}
            gap={1}
          >
            <Button
              variant="text"
              color="secondary"
              onClick={() => setOpenDeleteConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              sx={{
                color: '#fff',
              }}
            >
              Remove
            </Button>
          </Grid>
        </div>
      </Dialog>
    </Item>
  )
}

export default InsuranceInfo
